import { redirect } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import z from 'zod';

import { authResponseToSessionData } from './utils';

import { sessionMiddleware } from '@/middleware/session';
import type { TLAILoginResponse, TLAIPasswordUpdateRequiredResponse } from '@/types/api';
import { apiClient } from '@/utils/api-client';
import { createFormData, extractHTTPError, HTTPError } from '@/utils/http-client';
import { isDefaultRoute } from '@/utils/routes';
import { safeInvoke } from '@/utils/safe-invoke';
import { updateSessionDataHeaders } from '@/utils/session';

const LoginSchema = z.object({
  username: z.string(),
  password: z.string(),
  redirect_to: z.string().optional(),
});

export const loginServerFn = createServerFn({ method: 'POST' })
  .inputValidator(LoginSchema)
  .middleware([sessionMiddleware])
  .handler(async ({ data: { username, password, redirect_to }, context: { session } }) => {
    const apiClientInstance = await apiClient({ authenticated: false });

    const response = await safeInvoke(
      apiClientInstance.post<TLAILoginResponse>(
        '/auth/login',
        createFormData({
          username,
          password,
        }),
      ),
    );

    if (response.error) {
      if (
        response.error instanceof HTTPError &&
        response.error.status === 403 &&
        typeof response.error.data?.change_password_token === 'string'
      ) {
        return {
          password_update_required: true,
          change_password_token: response.error.data.change_password_token,
          username,
        } satisfies TLAIPasswordUpdateRequiredResponse;
      }

      console.error('[LOGIN] Error logging in:', extractHTTPError(response.error, 'An unexpected error occurred'));
      throw response.error;
    }

    if (!response.data) {
      throw new Error('No data returned from login endpoint');
    }

    const headers = updateSessionDataHeaders(new Headers(), {
      ...session,
      ...authResponseToSessionData(response.data),
    });

    if (redirect_to && !isDefaultRoute(redirect_to)) {
      throw redirect({ headers, href: redirect_to });
    }

    throw redirect({ to: '/', headers });
  });
